<template>
    <form class="flex overflow-hidden flex-col pt-40 rounded lg:w-10/12 lg:mx-auto"
          :class="[{ 'pb-64 lg:py-64': currentStep < 3, 'pb-20 lg:pb-4': currentStep === 3 }, `tr-multistep-form-step-${currentStep}`]"
          @keypress.enter.prevent>
        <slot />
        <input type="hidden" name="page" :value="currentPage">
        <input v-for="(value, name) in trackingParams" type="hidden" :key="name" :name="name" :value="value">
        <div v-if="success || errorMessage.length"
             class="flex items-center px-5 py-16 text-white lg:px-0 lg:grid lg:grid-cols-10 lg:gap-x-4 lg:h-113 bg-purple">
            <h2 class="lg:col-span-8 lg:col-start-2">
                <span v-if="errorMessage">{{ errorMessage }}</span>
                <slot v-else name="thankyou" />
            </h2>
        </div>
        <template v-else>
            <div class="px-5 pt-10 pb-5 text-white bg-purple lg:px-0 lg:grid lg:grid-cols-10 lg:gap-x-4">
                <h4 class="py-4 lg:col-start-2 lg:col-span-8" :class="{ 'space-y-4': currentStep >= 3 }">
                    <slot v-if="currentStep < 3" name="headline" />
                    <template v-else>
                        <span class="block">{{ firstStepOptions[selectedOptions[0]] }}</span>
                        <span class="block">{{ currentOptions[selectedOptions[1]] }}</span>
                    </template>
                </h4>
                <div v-if="!pelican"
                     class="flex relative justify-between items-center mt-4 text-2xl lg:col-span-8 lg:col-start-2">
                    <div v-for="step in 3"
                         :key="`step#${step}`"
                         @click="back(currentStep - step)"
                         class="font-bold rounded-full font-heading w-9 h-9 flex-shrink-0 pt-0.5 text-center relative z-10 border-2"
                         :class="{ 'text-yellow border-yellow': step === currentStep, 'text-purple border-yellow bg-yellow cursor-pointer': step < currentStep, 'bg-purple': step >= currentStep }">
                        {{ step }}
                    </div>
                    <div class="absolute w-full h-0.5 bg-white"></div>
                    <div class="absolute w-full h-0.5 bg-yellow origin-left transform transition-transform"
                         :class="{ 'scale-x-0': currentStep === 1, 'scale-x-50': currentStep === 2, 'scale-x-100': currentStep === 3 }"></div>
                </div>
            </div>
            <div class="pt-8 bg-white text-purple lg:grid lg:grid-cols-10 lg:gap-x-4">
                <h4 class="px-5 mb-10 lg:mb-0 lg:px-0 lg:col-span-8 lg:col-start-2" v-if="currentStep < 2">
                    <slot name="step1headline" />
                </h4>
                <div v-if="currentStep < 3 && !hasComplaint"
                     class="lg:col-span-8 lg:col-start-2">
                    <div class="grid auto-rows-fr gap-1 font-bold uppercase font-heading lg:grid-cols-2 lg:px-0 lg:py-7 lg:gap-x-5 lg:gap-y-4">
                        <template v-for="(option, i) in currentOptions">
                            <component :is="getOptionTag(i)"
                                       :href="getSupportClaimLink(i)"
                                       :target="getSupportClaimTarget(i)"
                                       :key="`1-${i}`"
                                       @click="select(i)"
                                       class="flex justify-between items-center p-5 transition-colors cursor-pointer bg-light lg:rounded hover:bg-purple hover:text-white">
                                <span>{{ option }}</span>
                                <icon name="back" class="flex-shrink-0 ml-4 w-5 h-3 transform rotate-180 fill-current" />
                            </component>
                        </template>
                    </div>
                    <div v-show="showFrame" class="col-span-2 row-span-6 py-8">
                        <iframe class="w-full h-200" :src="supportClaimLink" ref="iframe"/>
                    </div>
                </div>
                <div v-else-if="hasComplaint" class="px-5 lg:px-0 lg:col-span-8 lg:col-start-2">
                    <input type="hidden" :value="firstStepValue" name="purpose" id="purpose">
                    <slot name="complaint"
                          :select-file="selectFile"
                          :num-files="filenames.length"
                          :remove-file="removeFile"
                          :filesize-error="fileSizeError" />
                    <div class="my-6 lg:w-3/4">
                        <slot name="privacy" :toggle="toggle" />
                        <slot v-if="hasMarketing" name="marketing" />
                    </div>
                </div>
                <div v-else class="px-5 lg:px-0 lg:col-span-8 lg:col-start-2">
                    <input type="hidden" :value="firstStepValue" name="purpose" id="purpose">
                    <input type="hidden" :value="secondStepValue" name="customertype" id="customertype">
                    <slot name="contact" />
                    <template v-if="hasInquiry">
                        <slot v-if="pelican" name="pr-inquiry" />
                        <slot v-else name="inquiry" />
                    </template>
                    <slot name="info" />
                    <div class="my-6 lg:w-3/4">
                        <slot name="privacy" :toggle="toggle" />
                        <slot v-if="hasMarketing" name="marketing" />
                        <slot v-if="showSustainabilityCheckbox" name="sustainability"/>
                    </div>
                </div>
                <slot name="company-size-note"/>
                <div v-if="currentStep > firstStep"
                     class="flex justify-between px-5 my-8 lg:px-0 lg:col-span-8 lg:col-start-2">
                    <button class="button-secondary-purple" @click.prevent="back(1)">{{ backLabel }}
                    </button>
                    <button v-if="currentStep === 3 || hasComplaint"
                            class="button"
                            type="submit"
                            id="submit-form"
                            :disabled="!checked || loading"
                            @click.prevent="submit">
                        {{ submitLabel }}
                    </button>
                </div>
            </div>
        </template>
    </form>
</template>

<script lang="ts">
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import Icon from '../base/Icon.vue';
import axios from 'axios';
import Helper from '../../libs/Helper';

const FORM_ENDPOINT = '/.rest/api/v2/form';
const PR_FORM_ENDPOINT = '/.rest/api/v2/form/pr';
const TRACKING_PARAMS = ['gclid', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term'];

@Component({
    components: { Icon }
})
export default class MultiStepForm extends Vue {
    @Prop() firstStepOptions!: { [key: string]: string };
    @Prop() secondStepOptions!: { [key: string]: string };
    @Prop() otherOptions: { [key: string]: string };
    @Prop() supportClaimLink: string;
    @Prop({ default: false, type: Boolean }) showSupportClaimInIframe: boolean;
    @Prop({ required: true }) backLabel: string;
    @Prop({ required: true }) submitLabel: string;
    @Prop({ required: true }) serverError: string;
    @Prop({ default: false, type: Boolean }) pelican: boolean;
    @Prop({ default: false, type: Boolean }) showSustainabilityCheckbox: boolean;

    selectedOptions = [];
    success = false;
    checked = false;
    loading = false;
    errorMessage = '';
    firstStep = 1;
    filesizes: number[] = [];
    filenames: string[] = [];
    showFrame = false;

    select(option: string) {
        this.showFrame = false;
        if (!this.supportClaimLink || (option !== 'supportclaim')) {
            this.selectedOptions.push(option);
        } else if (this.showSupportClaimInIframe && this.supportClaimLink && (option === 'supportclaim')) {
            this.showFrame = true;
        }
    }

    back(steps = 1) {
        if (steps > this.selectedOptions.length) {
            return;
        }
        for (let i = 0; i < steps; i++) {
            this.selectedOptions.pop();
        }
    }

    async submit({ target }) {
        this.errorMessage = '';
        this.loading = true;
        const form: HTMLFormElement = target.form;
        const fd: FormData = new FormData(form);

        // check the total filesize and append the filenames to the form
        if (this.fileSizeError) {
            (form.elements.namedItem('file1') as HTMLInputElement).setCustomValidity('Upload limit is 5MB');
            return;
        }
        fd.set('filename', this.filenames.join(','));

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const encodedFd: URLSearchParams = new URLSearchParams(fd as any); // this works but type checking fails without `any`

        try {
            if (form.checkValidity()) {
                const res = await axios({
                    method: 'post',
                    url: `${this.$contextPath}${this.pelican ? PR_FORM_ENDPOINT : FORM_ENDPOINT}/${this.$site}/${this.$lang}`,
                    headers: {
                        'Content-Type': this.pelican ? 'multipart/form-data' : 'application/x-www-form-urlencoded'
                    },
                    data: this.pelican ? fd : encodedFd
                });
                // send form submit event to gtm
                if (window.dataLayer) {
                    const dataLayer = window.dataLayer || [];
                    dataLayer.push({
                        event: 'formSubmit',
                        purpose: fd.get('purpose'),
                        productinterest: fd.get('productinterest'),
                        customertype: fd.get('customertype')
                    });
                }
                if (res.data.success) {
                    this.success = true;
                } else {
                    this.errorMessage = this.serverError;
                }
                this.loading = false;
            } else {
                for (let i = 0; i < form.elements.length; i++) {
                    // trigger html validation messages
                    if (!(form.elements[i] as HTMLFormElement).reportValidity()) {
                        this.loading = false;
                        break;
                    }
                }
            }
        } catch (err) {
            this.errorMessage = this.serverError;
            this.loading = false;
        }

        // scroll to top of section
        let section: HTMLElement = form.offsetParent as HTMLElement;
        while (section.tagName.toLowerCase() !== 'section') {
            section = section.offsetParent as HTMLElement;
        }
        window.scrollTo({ top: section.offsetTop, behavior: 'smooth' });
    }

    toggle() {
        this.checked = !this.checked;
    }

    removeFile(name: string) {
        ((this.$el as HTMLFormElement).elements.namedItem(name) as HTMLInputElement).value = '';
        const index = this.filenames.findIndex(x => x === name);
        this.filenames.splice(index, 1);
        this.filesizes.splice(index, 1);
    }

    selectFile({ target }: { target: HTMLInputElement}, index: number) {
        const files = target.files;
        if (files.length) {
            const file = files.item(0);
            if (this.filenames[index]) {
                this.filenames[index] = file.name;
            } else {
                this.filenames.push(file.name);
            }
            if (this.filesizes[index]) {
                // use Vue.set to trigger reactivity, otherwise fileSizeError will not be updated
                Vue.set(this.filesizes, index, file.size);
            } else {
                this.filesizes.push(file.size);
            }
        } else {
            this.filenames.splice(index, 1);
            this.filesizes.splice(index, 1);
        }
    }

    getTrackingParams(params: URLSearchParams) {
        return TRACKING_PARAMS.reduce((obj, x) => {
            if (params.has(x)) {
                obj[x] = params.get(x);
            }
            return obj;
        }, {});
    }

    getOptionTag(option: string): string {
        return this.isShowSupportLink(option) ? 'a' : 'div';
    }

    getSupportClaimLink(option: string): string {
        return this.isShowSupportLink(option) ? this.supportClaimLink : null;
    }

    getSupportClaimTarget(option: string): string {
        return this.isShowSupportLink(option) ? '_blank' : null;
    }

    isShowSupportLink(option: string): boolean {
        return !!this.supportClaimLink && (option === 'supportclaim') && !this.showSupportClaimInIframe;
    }

    get currentOptions(): { [key: string]: string } {
        if (this.currentStep === 1) {
            return this.firstStepOptions;
        } else if (!this.pelican && this.selectedOptions[0] === this.fsok[2]) {
            return this.otherOptions;
        } else if (this.pelican && this.selectedOptions[0] === this.fsok[2]) {
            return this.otherOptions;
        } else {
            return this.secondStepOptions;
        }
    }

    get currentStep(): number {
        return this.selectedOptions.length + 1;
    }

    get firstStepValue(): string {
        return this.selectedOptions.length ? `${this.selectedOptions[0]}` : '';
    }

    get secondStepValue(): string {
        return this.selectedOptions.length > 1 ? `${this.selectedOptions[1]}` : '';
    }

    get hasInquiry(): boolean {
        if (this.pelican && this.currentStep === 3 && this.selectedOptions.length === 2) {
            return (this.selectedOptions[0] === this.fsok[0] && this.selectedOptions[1] === this.ssok[1]) ||
                (this.selectedOptions[0] === this.fsok[1] && this.selectedOptions[1] === this.ssok[1]);
        }
        return false;
    }

    get fsok(): string[] {
        return Object.keys(this.firstStepOptions);
    }

    get ssok(): string[] {
        return Object.keys(this.secondStepOptions);
    }

    get hasMarketing(): boolean {
        if (this.selectedOptions.length === 2) {
            return this.selectedOptions[0] === this.fsok[0];
        }
        return false;
    }

    get currentPage(): string {
        return location.pathname + location.search;
    }

    get hasComplaint(): boolean {
        return this.selectedOptions.includes('complaint');
    }

    get trackingParams(): { [key: string]: string } {
        const cookie = Helper.getCookie('lzcp');
        if (cookie) {
            const params = new URLSearchParams(cookie.replace(/^.*\?/, ''));
            const cookieParams = this.getTrackingParams(params);
            const urlParams = this.getTrackingParams(new URLSearchParams(location.search));
            return { ...cookieParams, ...urlParams };
        }
        return {};
    }

    get fileSizeError(): boolean {
        return this.filesizes.reduce((a, b) => a + b, 0) > (5 * 1024 * 1024);
    }
}
</script>
